/**
 * Shared ROAS (Return on Ad Spend) calculation used by Google
 * `CampaignCard` and `MetaCampaignCard`.
 *
 * The same two-tier logic applies on both platforms:
 *
 *   1. **Conversion-based** (preferred): `conversions_value / cost`.
 *      Used when the user has opted into conversion-based ROAS AND any
 *      campaign in the same platform's set has reported a non-zero
 *      conversions_value (so the calculation isn't comparing apples to
 *      "no data"). For Meta entries, `conversions_value` is the
 *      server-mapped alias of `purchases_value` — see
 *      `construct_app_configuration_json__meta_ads` in
 *      `src/flouri/account_configuration.py`.
 *
 *   2. **Click-based fallback**: `(clicks * revenue_per_click) / cost`.
 *      Used when conversion tracking isn't available or the user
 *      prefers click-based. `revenue_per_click` is a per-member setting
 *      stored on the saved app_config (`revenue_per_click` top-level
 *      key); the default at `ROAS_CONFIG.DEFAULT_REVENUE_PER_CLICK` is
 *      a conservative $0.10/click.
 *
 * The "consistency across campaigns" rationale: if some campaigns in
 * the same grid used conversion-based and others used click-based,
 * comparing their ROAS would be misleading. The caller-supplied
 * `conversionTrackingAvailable` flag forces all cards in a platform's
 * grid to land on the same calculation method.
 */
import { ROAS_CONFIG } from '@/config/constants';

export interface ComputeRoasInput {
  /** Total spend across the reporting window. Both platforms expose
   * this as their "cost" / "spend" field. */
  cost: number;
  /** Total clicks across the window. */
  clicks: number;
  /** Total conversion / purchase value. Meta passes `purchases_value`
   * here, mapped to `conversions_value` by the constructor. */
  conversionsValue: number | null | undefined;
  /** Member's per-click revenue setting (from app_config). Falls back
   * to `ROAS_CONFIG.DEFAULT_REVENUE_PER_CLICK` ($0.10) when undefined. */
  revenuePerClick?: number;
  /** User's calculation-method preference. When false, this card uses
   * click-based ROAS even if conversion data exists. */
  useConversionBasedRoas: boolean;
  /** Whether ANY campaign in this card's platform-set has reported a
   * non-zero `conversions_value`. Forces consistency across cards. */
  conversionTrackingAvailable: boolean;
}

export interface ComputeRoasResult {
  /** The computed ROAS as a multiplier (e.g. 2.5 → "2.50x"), or null
   * when neither branch can produce a meaningful value (zero spend,
   * zero clicks). */
  roas: number | null;
  /** Which branch produced the value. Drives the "Click-based · $X/click"
   * helper text on the expanded card. */
  isUsingClickBasedRoas: boolean;
  /** The per-click revenue value that was used (or would be used by
   * the click-based branch). Surfaces in the expanded card's helper
   * text so users see "Click-based · $0.10/click". */
  effectiveRevenuePerClick: number;
}

/**
 * Format a ROAS multiplier for display.
 *
 *   null              → "N/A"
 *   0                 → "0.00x"
 *   |r| >= 0.01       → 2 decimals (e.g. "2.50x", "-0.50x")
 *   0 < |r| < 0.01    → enough decimals to show 2 significant figures of
 *                       the magnitude (e.g. 0.0028 → "0.0028x",
 *                       -0.00009 → "-0.000090x").
 *
 * The variable-precision branch only fires for sub-1% magnitudes, where
 * the fixed 2-decimal format would round to "0.00x" and look like a bug.
 *
 * Threshold + precision are computed on `Math.abs(roas)` so negative
 * ROAS (e.g. refunds outpacing gross revenue → conversions_value < 0)
 * formats correctly. Without the abs, `Math.log10(negative)` is NaN and
 * the call falls through to `toFixed(NaN)` — which doesn't throw (NaN
 * coerces to 0 via ToIntegerOrInfinity), but rounds to an integer
 * string like "-1x" that hides the actual magnitude.
 */
export function formatRoasDisplay(roas: number | null): string {
  if (roas === null) return 'N/A';
  if (roas === 0) return '0.00x';
  if (Math.abs(roas) >= 0.01) return `${roas.toFixed(2)}x`;
  // Need `floor(-log10(|r|)) + 2` decimals to keep 2 sig figs visible.
  // Cap at 8 to bound the string length on pathologically small values.
  const decimals = Math.min(8, Math.floor(-Math.log10(Math.abs(roas))) + 2);
  return `${roas.toFixed(decimals)}x`;
}

export function computeCampaignRoas({
  cost,
  clicks,
  conversionsValue,
  revenuePerClick,
  useConversionBasedRoas,
  conversionTrackingAvailable,
}: ComputeRoasInput): ComputeRoasResult {
  const effectiveRevenuePerClick =
    revenuePerClick ?? ROAS_CONFIG.DEFAULT_REVENUE_PER_CLICK;

  const shouldUseConversionBased =
    useConversionBasedRoas && conversionTrackingAvailable;

  if (shouldUseConversionBased && cost > 0) {
    return {
      roas: (conversionsValue ?? 0) / cost,
      isUsingClickBasedRoas: false,
      effectiveRevenuePerClick,
    };
  }

  if (clicks > 0 && cost > 0) {
    const estimatedRevenue = clicks * effectiveRevenuePerClick;
    return {
      roas: estimatedRevenue / cost,
      isUsingClickBasedRoas: true,
      effectiveRevenuePerClick,
    };
  }

  return {
    roas: null,
    isUsingClickBasedRoas: false,
    effectiveRevenuePerClick,
  };
}
